"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { ArrowLeftIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import { Footer } from "@/components/Footer";
import { legalDocuments } from "@/lib/legal-content";
import { sanitizeHtml } from "@/lib/sanitize";

function parseMarkdown(text: string) {
  if (!text) return "";

  const lines = text.split('\n');
  let html = "";
  let inList = false;

  lines.forEach(line => {
    const trimmed = line.trim();

    if (trimmed.startsWith('# ')) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h1 class="text-2xl font-bold mb-4 mt-6 text-gray-900">${trimmed.substring(2)}</h1>`;
    } else if (trimmed.startsWith('## ')) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h2 class="text-lg font-semibold mb-2 mt-6 text-gray-900">${trimmed.substring(3)}</h2>`;
    } else if (trimmed.startsWith('### ')) {
      if (inList) { html += "</ul>"; inList = false; }
      html += `<h3 class="text-base font-medium mb-2 mt-4 text-gray-900">${trimmed.substring(4)}</h3>`;
    } else if (trimmed.startsWith('- ')) {
      if (!inList) { html += `<ul class="list-disc pl-5 space-y-1 mb-4 text-gray-700">`; inList = true; }
      html += `<li>${trimmed.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>`;
    } else if (trimmed === "") {
      if (inList) { html += "</ul>"; inList = false; }
    } else {
      if (inList) { html += "</ul>"; inList = false; }
      const content = trimmed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      if (content) {
        html += `<p class="mb-4 text-gray-700 leading-relaxed">${content}</p>`;
      }
    }
  });

  if (inList) { html += "</ul>"; }

  return html;
}

export default function TermsAndConditionsPage() {
  const document = legalDocuments.studentTerms;
  const contentHtml = useMemo(() => parseMarkdown(document.content), [document]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[90px]">
      {/* Navigation Bar */}
      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-[60px] sm:top-[80px] z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link
              href="/"
              className="flex items-center gap-2 text-gray-900 hover:text-orange-500 transition-all duration-200"
            >
              <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="font-medium text-sm sm:text-base">
                Back to Home
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <article className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200 overflow-hidden mb-8">
          {/* Header */}
          <div className="p-4 sm:p-6 pb-3 sm:pb-4 border-b border-gray-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-orange-500/10 text-orange-600 text-xs sm:text-sm font-semibold rounded-lg">
                    Legal
                  </span>
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                    Version {document.version}
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                    Effective: {document.effectiveDate}
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                    Last Updated: {document.lastUpdatedAt}
                  </div>
                </div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">
                  {document.title}
                </h1>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            <div
              className="prose max-w-none text-gray-900 text-xs sm:text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(contentHtml) }}
            />
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}
